import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  selectRelatedObjectConfigurationTypeFilterOptions,
  selectRelatedObjectFilterOptions,
  selectSingleObject,
  selectSingleObjectActiveTab,
} from '../../../../reducers/documentation/documentationReducer';
import { selectCrmOrgAlerts } from '../../../../reducers/global/alertsReducers';
import { selectAssignments } from '../../../../reducers/global/assignmentReducers';
import { selectCrmOrgAutomations } from '../../../../reducers/global/automationReducers';
import { selectDedup, selectMatching } from '../../../../reducers/global/dedupMatchingReducers';
import { DocumentationTabTypes } from '../../../../types/enums/DocumentationTabTypes';
import {
  getFieldsTabFilters,
  getApexTabFilters,
  getSalesforceAutomationsTabFilters,
  getValidationRulesTabFilters,
  getLayoutsTabFilters,
  getRecordTypesTabFilters,
  getRelatedObjectsFilters,
  getDocumentationAutomationFilters,
  dedupMatchingFilters,
} from './filtersOptions';
import { selectScheduledAssignments } from '../../../../reducers/global/scheduledAssignmentReducers';
import { getDeployedVersions, getStepFullNameFromId, getParentFromStepId } from './utils';
import { selectParsedFields } from '../../../../reducers/global/parserReducer';
import { useDocumentationContext } from '../../context/DocumentationContext';
import { DocumentationFiltersTypesMap } from './filterTypes';
import { useDocumentationFiltersContext } from './DocumentationFiltersContext';

/**
 * Hook that generates filter definitions based on current documentation context
 * Moved from DocumentationFiltersContext to keep context only for state management
 */
export const useDocumentationFilters = (crmOrgId: string) => {
  const { recordTypesUsedInCanvasByObjectName, stepNamesUsedInCanvasByObjectName } =
    useDocumentationContext();
  const { filterValues } = useDocumentationFiltersContext();
  const objectTypeName = useSelector(selectSingleObject);
  const activeTab = useSelector(selectSingleObjectActiveTab);
  const fieldsWithUtilization = useSelector(selectParsedFields(crmOrgId));
  const automations = useSelector(selectCrmOrgAutomations(crmOrgId));
  const alerts = useSelector(selectCrmOrgAlerts(crmOrgId));
  const dedup = useSelector(selectDedup);
  const matching = useSelector(selectMatching);
  const assignments = useSelector(selectAssignments);
  const scheduledAssignments = useSelector(selectScheduledAssignments);
  const relatedObjectsOptions = useSelector(selectRelatedObjectFilterOptions);
  const relatedObjectConfigurationTypeFilterOptions = useSelector(
    selectRelatedObjectConfigurationTypeFilterOptions,
  );

  const deployedAutomations = useMemo(
    () => ({
      automations: getDeployedVersions(automations),
      alerts: getDeployedVersions(alerts),
      matching: getDeployedVersions(matching),
      dedup: getDeployedVersions(dedup),
      assignments: getDeployedVersions(assignments),
      scheduledAssignments: getDeployedVersions(scheduledAssignments),
    }),
    [automations, alerts, matching, dedup, assignments, scheduledAssignments],
  );

  const recordTypesItems = useMemo(() => {
    return !!recordTypesUsedInCanvasByObjectName?.[objectTypeName?.objectType ?? '']
      ? Object.entries(recordTypesUsedInCanvasByObjectName[objectTypeName?.objectType ?? '']).map(
          ([key, value]) => ({
            value: key,
            label: value,
          }),
        )
      : [];
  }, [recordTypesUsedInCanvasByObjectName, objectTypeName?.objectType]);

  const stepNamesItems = useMemo(() => {
    return !!stepNamesUsedInCanvasByObjectName?.[objectTypeName?.objectType ?? '']
      ? Object.entries(stepNamesUsedInCanvasByObjectName[objectTypeName?.objectType ?? '']).map(
          ([, value]) => {
            return {
              value: value,
              label: getStepFullNameFromId(value),
            };
          },
        )
      : [];
  }, [stepNamesUsedInCanvasByObjectName, objectTypeName?.objectType]);

  const baseFilters = useMemo(() => {
    if (!activeTab) return {};

    const recordTypesValues = filterValues?.recordTypes?.selectedValues ?? [];
    const stepNamesFromRecordTypes =
      recordTypesValues.length > 0
        ? stepNamesItems.filter((item) =>
            recordTypesValues.includes(getParentFromStepId(item.value)),
          )
        : stepNamesItems;

    switch (activeTab) {
      case DocumentationTabTypes.FIELDS: {
        const fields = fieldsWithUtilization?.[objectTypeName?.objectType as any] ?? [];
        return getFieldsTabFilters(fields);
      }

      case DocumentationTabTypes.APEX:
        return getApexTabFilters(recordTypesItems, stepNamesFromRecordTypes);

      case DocumentationTabTypes.LAYOUTS:
        return getLayoutsTabFilters(recordTypesItems);

      case DocumentationTabTypes.SF_AUTOMATIONS:
        return getSalesforceAutomationsTabFilters(recordTypesItems, stepNamesFromRecordTypes);

      case DocumentationTabTypes.VALIDATION_RULES:
        return getValidationRulesTabFilters(recordTypesItems, stepNamesFromRecordTypes);

      case DocumentationTabTypes.SWEEP_AUTOMATIONS:
        return getDocumentationAutomationFilters(
          deployedAutomations.automations,
          recordTypesItems,
          stepNamesFromRecordTypes,
        );

      case DocumentationTabTypes.PLAYBOOK_ALERTS:
        return getDocumentationAutomationFilters(
          deployedAutomations.alerts,
          recordTypesItems,
          stepNamesFromRecordTypes,
        );

      case DocumentationTabTypes.ASSIGNMENTS:
      case DocumentationTabTypes.SCHEDULED_ASSIGNMENTS:
        return getDocumentationAutomationFilters(
          deployedAutomations.alerts,
          recordTypesItems,
          stepNamesFromRecordTypes,
        );

      case DocumentationTabTypes.MATCHING_DEDUPE:
        return dedupMatchingFilters({
          recordTypesItems,
        });

      case DocumentationTabTypes.RECORD_TYPES:
        return getRecordTypesTabFilters();

      case DocumentationTabTypes.RELATED_OBJECTS:
        return getRelatedObjectsFilters(
          relatedObjectsOptions?.filter((obj) => obj.value !== objectTypeName?.objectType) ?? [],
          relatedObjectConfigurationTypeFilterOptions ?? [],
        );

      default:
        return {} as any;
    }
  }, [
    activeTab,
    fieldsWithUtilization,
    objectTypeName?.objectType,
    deployedAutomations,
    relatedObjectsOptions,
    relatedObjectConfigurationTypeFilterOptions,
    recordTypesItems,
    stepNamesItems,
    filterValues,
  ]);

  /**
   * Filter out Record Types and Step filters if canvasFunnelMap is not provided
   */
  const filters = useMemo(() => {
    if (!recordTypesUsedInCanvasByObjectName && !stepNamesUsedInCanvasByObjectName) {
      const filteredFilters = { ...baseFilters };

      // Remove Record Types filter if canvasFunnelMap is not provided
      if (filteredFilters[DocumentationFiltersTypesMap.recordTypes]) {
        delete filteredFilters[DocumentationFiltersTypesMap.recordTypes];
      }

      // Remove Step filter if canvasFunnelMap is not provided
      if (filteredFilters[DocumentationFiltersTypesMap.steps]) {
        delete filteredFilters[DocumentationFiltersTypesMap.steps];
      }

      return filteredFilters;
    }

    return baseFilters;
  }, [baseFilters, recordTypesUsedInCanvasByObjectName, stepNamesUsedInCanvasByObjectName]);

  const automationsList = useMemo(() => {
    if (!activeTab) return [];

    switch (activeTab) {
      case DocumentationTabTypes.SWEEP_AUTOMATIONS:
        return deployedAutomations.automations;
      case DocumentationTabTypes.PLAYBOOK_ALERTS:
        return deployedAutomations.alerts;
      case DocumentationTabTypes.MATCHING_DEDUPE:
        return [...deployedAutomations.matching, ...deployedAutomations.dedup];
      case DocumentationTabTypes.ASSIGNMENTS:
        return deployedAutomations.assignments;
      case DocumentationTabTypes.SCHEDULED_ASSIGNMENTS:
        return deployedAutomations.scheduledAssignments;
      default:
        return [];
    }
  }, [activeTab, deployedAutomations]);

  return {
    filters,
    automations: automationsList,
    crmOrgId,
    recordTypesUsedInCanvasByObjectName,
    stepNamesUsedInCanvasByObjectName,
  };
};
